'use client';

import dynamic from 'next/dynamic';
import { default as NextLink } from 'next/link';
import { useRouter } from 'next/navigation';
import type { FormEvent } from 'react';
import { Link, MemoryRouter, Route, Routes } from 'react-router-dom';
import { create } from 'zustand';

import { Aparat, ArrowLeftSmall, ArrowRightSmall, Instagram, Telegram, X } from '@zoomit/icons';
import { Sheet } from '@zoomit/mobile-sheet';
import { ToggleThemeMobile } from '@zoomit/web-kit';

import { SearchDialogSkeleton } from '../../search/SearchDialogSkeleton';
import { NavbarWithClose, SearchBar } from '../Navbar';

const SearchDialog = dynamic(() => import('../../search/SearchDialog').then((m) => m.SearchDialog), {
  ssr: false,
  loading: () => <SearchDialogSkeleton />,
});

type ShowSearchState = {
  showSearch: boolean;
  setShowSearch: (state: boolean) => void;
};

const useShowSearch = create<ShowSearchState>((set) => ({
  showSearch: false,
  setShowSearch: (value) => set(() => ({ showSearch: value })),
}));

const SOCIALS = [
  { name: 'instagram', icon: Instagram, href: 'https://www.instagram.com/myzoobin/' },
  { name: 'aparat', icon: Aparat, href: 'https://www.aparat.com/zoomit' },
  { name: 'telegram', icon: Telegram, href: 'https://t.me/thezoomit/' },
  { name: 'X', icon: X, href: 'https://x.com/thezoomit' },
];

const getLinks = () => [
  { title: 'راهنمای خرید', href: 'https://www.zoomit.ir/buying-guide' },
  { title: 'بررسی', href: 'https://www.zoomit.ir/review' },
  { title: 'فروشگاه‌ها', href: '/shop-list' },
  { title: 'مجله زومیت', href: 'https://www.zoomit.ir/' },
  { title: 'زوبین', href: '/others-app', hasArrow: true },
];

const ZOOMIT_LINKS = [
  { title: 'تبلیغات', href: 'https://www.zoomit.ir/advertisement' },
  { title: 'فرصت‌های شغلی', href: 'https://www.zoomit.ir/hire' },
  { title: 'درباره ما', href: '/about-us' },
  { title: 'تماس با‌ما', href: 'https://www.zoomit.ir/contact-us' },
];

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const OthersSheet = (props: Props) => {
  const { isOpen, onClose } = props;
  const { showSearch } = useShowSearch();

  return (
    <Sheet isOpen={isOpen} onClose={onClose} disableDrag>
      <Sheet.Container isFullScreen preventOverlayClick={!showSearch}>
        <MemoryRouter>
          <Sheet.Header>
            <Routes>
              <Route path="/" element={<NavOthersHeader onClose={onClose} />} />
              <Route path="/others-app" element={<NavOthersAppHeader onClose={onClose} />} />
            </Routes>
          </Sheet.Header>
          <Sheet.Content>
            <Routes>
              <Route path="/" element={<NavOthers onCloseSheet={onClose} />} />
              <Route path="/others-app" element={<NavOthersApp onCLoseSheet={onClose} />} />
            </Routes>
          </Sheet.Content>
        </MemoryRouter>
      </Sheet.Container>
      <Sheet.Backdrop />
    </Sheet>
  );
};

const NavOthers = ({ onCloseSheet }: { onCloseSheet: () => void }) => {
  return (
    <div className="bg-background2 flex h-full flex-col">
      <div className="text-body flex grow flex-col justify-between px-5">
        <div>
          {getLinks().map((item) =>
            item.hasArrow ? (
              <Link to={item.href} className="flex items-center justify-between py-3.5" key={item.title}>
                <span>{item.title}</span>

                {item.hasArrow && <ArrowLeftSmall size="sm" />}
              </Link>
            ) : (
              <NextLink
                href={item.href}
                className="flex items-center justify-between py-3.5"
                key={item.title}
                onClick={onCloseSheet}
              >
                <span>{item.title}</span>
              </NextLink>
            ),
          )}

          <div className="border-borderPrimary mt-4 border-t pt-4">
            <ToggleThemeMobile />
          </div>
        </div>

        <div className="border-borderPrimary flex items-center justify-between border-t px-4 py-6">
          {SOCIALS.map((item) => (
            <Link
              aria-label={item.name}
              to={item.href}
              key={item.name}
              rel="nofollow"
              target="_blank"
              title={item.name}
            >
              {<item.icon size="dl" />}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

type HeaderProps = { onClose: () => void };

const NavOthersHeader = (props: HeaderProps) => {
  const { onClose } = props;
  const router = useRouter();
  const { setShowSearch, showSearch } = useShowSearch();

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const query = formData.get('query');
    if (query) {
      router.push(`/search/${query}`);
      onClose();
    }
  };

  return (
    <>
      {showSearch && <SearchDialog onClose={() => setShowSearch(false)} />}
      <NavbarWithClose onClose={onClose} />
      <form onSubmit={handleSubmit} className="bg-background2">
        <SearchBar className="p-4" name="query" onFocus={() => setShowSearch(true)} />
      </form>
    </>
  );
};

const NavOthersApp = ({ onCLoseSheet }: { onCLoseSheet: () => void }) => {
  return (
    <div className="bg-background2 flex h-full flex-col">
      <div className="text-body mt-4 px-5">
        {ZOOMIT_LINKS.map((item) => (
          <NextLink
            href={item.href}
            className="flex items-center justify-between py-3.5"
            key={item.title}
            onClick={onCLoseSheet}
          >
            <span>{item.title}</span>
          </NextLink>
        ))}
      </div>
    </div>
  );
};

const NavOthersAppHeader = (props: HeaderProps) => {
  const { onClose } = props;

  return (
    <>
      <NavbarWithClose onClose={onClose} />
      <div className="bg-background1 text-body grid grid-cols-3 items-center p-4">
        <Link
          to=".."
          role="button"
          className="text-subhead text-grey1 flex w-fit cursor-pointer items-center gap-2"
        >
          <ArrowRightSmall size="md" />
          بازگشت
        </Link>

        <span className="justify-self-center">زوبین</span>
      </div>
    </>
  );
};
